/**
 * autoScanOnOpenPref — "check my Screenshots album when Nexus Dex opens"
 * (2026-09-19 owner ask).
 *
 * Auto Scan was a button: every sweep needed a trip to My Pokémon > Upload.
 * With this ON, the same sweep runs by itself on launch and on every return
 * to the foreground (autoScanOnOpen.ts), so a trainer who screenshots a
 * Pokémon in GO and switches back finds it already saved.
 *
 * THREE STATES, NOT TWO. The key is absent until the trainer has either run
 * Auto Scan once by hand or touched the Settings switch:
 *
 *   absent  -> off, undecided. The first manual Auto Scan adopts ON (the
 *              owner's rule: turning Auto Scan on means keeping it on) and
 *              says so in a toast, so nobody discovers a launch-time sweep
 *              by surprise.
 *   "on"    -> the on-open sweep runs (when Photos access is already granted;
 *              a launch never prompts).
 *   "off"   -> the trainer said no, and a later manual sweep must NOT flip it
 *              back: an explicit choice beats the adoption default.
 *
 * Device-local like every other scan pref (dexScanSettings.ts explains why:
 * Photos access is a per-device grant, so the switch that leans on it is
 * per-device too).
 */
const STORAGE_KEY = "nexus.autoScanOnOpen.v1";
export const AUTO_SCAN_ON_OPEN_EVENT = "nexus:auto-scan-on-open-changed";

function read(): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function emit(): void {
  try {
    window.dispatchEvent(new Event(AUTO_SCAN_ON_OPEN_EVENT));
  } catch {
    /* SSR / tests without a window */
  }
}

export function isAutoScanOnOpenEnabled(): boolean {
  return read() === "on";
}

/** True once the trainer (or the adoption default) has decided either way. */
export function hasAutoScanOnOpenChoice(): boolean {
  const v = read();
  return v === "on" || v === "off";
}

export function setAutoScanOnOpenEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "on" : "off");
  } catch {
    /* storage blocked: the sweep simply stays manual */
  }
  emit();
}

/**
 * The adoption default: called after a MANUAL Auto Scan starts. Turns the
 * on-open sweep on only while the trainer has never chosen, and reports
 * whether it did so the caller can say so once.
 */
export function adoptAutoScanOnOpenDefault(): boolean {
  if (hasAutoScanOnOpenChoice()) return false;
  setAutoScanOnOpenEnabled(true);
  return true;
}

/** Test seam. */
export function __resetAutoScanOnOpenPrefForTest(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
